<template>
  <div>
    <v-row v-if="isLoading" class="d-flex justify-center mb-2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="50">
      </v-progress-circular>
    </v-row>
    <template v-else-if="evaluationData">
      <!-- Summary -->
      <div class="metric-boxes">
        <div
          v-if="evaluationData.score"
          class="metric-box metric-box--score metric-box--light">
          <div class="metric-label">Score</div>
          <div class="metric-value">{{ evaluationData.score }}</div>
        </div>
        <div
          v-if="evaluationData.trackName"
          class="metric-box metric-box--blue">
          <div class="metric-label">Track</div>
          <div class="metric-value">{{ evaluationData.trackName }}</div>
          <div v-if="evaluationData.functionName" class="metric-subvalue">in {{ evaluationData.functionName }}</div>
        </div>
        <div
          v-if="evaluationData.suggestedLevelAbbr"
          class="metric-box metric-box--suggested-level">
          <div class="metric-label">Suggested Level</div>
          <div class="metric-value">{{ evaluationData.suggestedLevelAbbr }}</div>
        </div>
        <div
          v-if="evaluationData.selectedLevelAbbr"
          class="metric-box metric-box--light metric-box--selected-level">
          <div class="metric-label">Selected Level</div>
          <div class="metric-value">{{ evaluationData.selectedLevelAbbr }}</div>
        </div>
      </div>

      <v-alert
        v-if="evaluationData.comments"
        dense
        text
        type="info"
        class="body-2 mb-4">
        {{ evaluationData.comments }}
      </v-alert>

      <!-- Factors -->
      <template v-if="evaluationData.classificationAnswers && evaluationData.classificationAnswers.length">
        <h4 class="subtitle-2 mb-2">Factors</h4>
        <v-simple-table dense class="mb-4">
          <template v-slot:default>
            <thead>
              <tr>
                <th>Factor</th>
                <th>Selected Level</th>
                <th>Created By</th>
                <th>Justification</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in factorRows"
                :key="row.key"
                :class="{ 'inactive-row': !row.item.active }">
                <template v-if="!row.comment">
                  <td>
                    {{ row.item.factorName }}
                    <v-chip v-if="!row.item.active" x-small label color="orange" text-color="white" class="ml-1">Inactive</v-chip>
                  </td>
                  <td>{{ row.item.selectedLevel }}</td>
                  <td>
                    {{ row.item.created_by }}
                    <div class="caption grey--text text--darken-1">{{ row.item.created_date }}</div>
                  </td>
                  <td class="text-center">
                    <v-btn
                      v-if="row.item.comments"
                      icon
                      small
                      @click="toggleComment(row.toggleKey)">
                      <v-icon small>{{ isCommentExpanded(row.toggleKey) ? 'mdi-comment-remove-outline' : 'mdi-comment-text-outline' }}</v-icon>
                    </v-btn>
                  </td>
                </template>
                <td v-else colspan="4">
                  <div class="subfactor-justification">
                    <v-icon x-small color="grey darken-1" class="subfactor-justification-icon">mdi-comment-quote-outline</v-icon>
                    <div>
                      <div class="subfactor-justification-label">Justification<span v-if="provenanceLabel(row.item.rationaleProvenance, { short: true })" class="subfactor-justification-atlas"><v-icon x-small color="indigo">mdi-compass-outline</v-icon> {{ provenanceLabel(row.item.rationaleProvenance, { short: true }) }}</span></div>
                      <div class="subfactor-justification-text">{{ row.item.comments }}</div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </template>

      <!-- Sub-Factors -->
      <template v-if="evaluationData.standardAnswers && evaluationData.standardAnswers.length">
        <h4 class="subtitle-2 mb-2">Sub-Factors</h4>
        <v-simple-table dense class="mb-4">
          <template v-slot:default>
            <thead>
              <tr>
                <th>Factor</th>
                <th>Sub-Factor</th>
                <th>Level</th>
                <th>Level Description</th>
                <th>Created By</th>
                <th>Justification</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in subFactorRows"
                :key="row.key"
                :class="{ 'inactive-row': !row.item.active }">
                <template v-if="!row.comment">
                  <td>
                    {{ row.item.factorName }}
                    <v-chip v-if="!row.item.active" x-small label color="orange" text-color="white" class="ml-1">Inactive</v-chip>
                  </td>
                  <td>{{ row.item.subFactorName }}</td>
                  <td>
                    <v-chip :color="getLevelColor(Math.round(row.item.selectedLevelScore))" dark x-small>
                      {{ Math.round(row.item.selectedLevelScore) }}
                    </v-chip>
                  </td>
                  <td>{{ row.item.selectedLevelName }}</td>
                  <td>
                    {{ row.item.created_by }}
                    <div class="caption grey--text text--darken-1">{{ row.item.created_date }}</div>
                  </td>
                  <td class="text-center">
                    <v-btn
                      v-if="row.item.comments"
                      icon
                      small
                      @click="toggleComment(row.toggleKey)">
                      <v-icon small>{{ isCommentExpanded(row.toggleKey) ? 'mdi-comment-remove-outline' : 'mdi-comment-text-outline' }}</v-icon>
                    </v-btn>
                  </td>
                </template>
                <td v-else colspan="6">
                  <div class="subfactor-justification">
                    <v-icon x-small color="grey darken-1" class="subfactor-justification-icon">mdi-comment-quote-outline</v-icon>
                    <div>
                      <div class="subfactor-justification-label">Justification<span v-if="provenanceLabel(row.item.rationaleProvenance, { short: true })" class="subfactor-justification-atlas"><v-icon x-small color="indigo">mdi-compass-outline</v-icon> {{ provenanceLabel(row.item.rationaleProvenance, { short: true }) }}</span></div>
                      <div class="subfactor-justification-text">{{ row.item.comments }}</div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </template>

      <!-- Guided Answers -->
      <template v-if="evaluationData.guidedAnswers && evaluationData.guidedAnswers.length">
        <h4 class="subtitle-2 mb-2">Guided Answers</h4>
        <v-simple-table dense class="mb-4">
          <template v-slot:default>
            <thead>
              <tr>
                <th>Question</th>
                <th>Answer</th>
                <th>Created By</th>
                <th>Comments</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in guidedRows"
                :key="row.key"
                :class="{ 'inactive-row': !row.item.active }">
                <template v-if="!row.comment">
                  <td>
                    {{ row.item.questionId }}
                    <v-chip v-if="!row.item.active" x-small label color="orange" text-color="white" class="ml-1">Inactive</v-chip>
                  </td>
                  <td>{{ row.item.selectedKey }}</td>
                  <td>
                    {{ row.item.created_by }}
                    <div class="caption grey--text text--darken-1">{{ row.item.created_date }}</div>
                  </td>
                  <td class="text-center">
                    <v-btn
                      v-if="row.item.comments"
                      icon
                      small
                      @click="toggleComment(row.toggleKey)">
                      <v-icon small>{{ isCommentExpanded(row.toggleKey) ? 'mdi-comment-remove-outline' : 'mdi-comment-text-outline' }}</v-icon>
                    </v-btn>
                  </td>
                </template>
                <td v-else colspan="4">
                  <div class="subfactor-justification">
                    <v-icon x-small color="grey darken-1" class="subfactor-justification-icon">mdi-comment-quote-outline</v-icon>
                    <div>
                      <div class="subfactor-justification-label">Comments</div>
                      <div class="subfactor-justification-text">{{ row.item.comments }}</div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </template>
    </template>
    <ResponseHandler :serviceResponse="response"></ResponseHandler>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import axios from "axios";
import ResponseHandler from "@/components/ResponseHandler"; // @ is an alias to /src
import utils from "@/common/utils.js";
import { provenanceLabel as rationaleProvenanceLabel } from "@rm-ui/levelling/rationaleDraft";

export default {
  name: "DocumentLevellingEvaluationDetails",
  components: {
    ResponseHandler,
  },
  props: {
    doc_id: { type: Number, required: true },
    document_lf_evaluation_id: { type: Number, required: true }
  },
  setup(props, { emit }) {
    const isLoading = ref(false);
    const response = ref(null);
    const evaluationData = ref(null);
    const expandedComments = ref([]);

    const isCommentExpanded = (key) => expandedComments.value.includes(key);
    // AI rationale provenance (PD-1301): "Drafted by Atlas" / "Atlas draft, edited".
    const provenanceLabel = rationaleProvenanceLabel;

    const getLevelColor = (level) => {
      const colors = ["grey", "green-grey", "blue-grey", "teal", "blue", "indigo", "deep-purple", "black"];
      return colors[level] || "grey";
    };

    const toggleComment = (key) => {
      const i = expandedComments.value.indexOf(key);
      if (i === -1) expandedComments.value.push(key);
      else expandedComments.value.splice(i, 1);
    };

    // Flatten each answer set into a list of table rows: a data row, optionally
    // followed by its (expanded) comment row. Iterating <tr> over this keeps the
    // :key on a real element (one <tbody>), and gives every row a stable unique
    // key even when inactive answers duplicate a factor/sub-factor id.
    const buildRows = (answers, prefix) =>
      (answers || []).flatMap((item, i) => {
        const key = `${prefix}_${i}`;
        const rows = [{ key, toggleKey: key, item, comment: false }];
        if (item.comments && isCommentExpanded(key)) {
          rows.push({ key: `${prefix}c_${i}`, item, comment: true });
        }
        return rows;
      });

    const factorRows = computed(() => buildRows(evaluationData.value?.classificationAnswers, "f"));
    const subFactorRows = computed(() => buildRows(evaluationData.value?.standardAnswers, "s"));
    const guidedRows = computed(() => buildRows(evaluationData.value?.guidedAnswers, "g"));

    const getData = () => {
      isLoading.value = true;
      axios
        .get(`document/getlfEvaluationAudit/${props.document_lf_evaluation_id}`)
        .then((resp) => {
          if (resp.data.Status === "OK") {
            evaluationData.value = resp.data.Data;
          } else {
            alert(resp.data.Message);
          }
          response.value = resp.data;
          isLoading.value = false;
        })
        .catch((err) => {
          if (err.response && err.response.status === 401) {
            emit("sessionExpired", err);
          } else {
            console.log(err);
            response.value = err.response
              ? err.response.data
              : { message: "Unexpected Error" };
          }
          isLoading.value = false;
        });
    };

    onMounted(() => {
      getData();
    });

    return { utils, isLoading, response, getData, evaluationData, isCommentExpanded, toggleComment, getLevelColor, factorRows, subFactorRows, guidedRows, provenanceLabel };
  },
};
</script>
<style scoped lang="scss">
.metric-boxes {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.metric-box {
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
}

.metric-box--small {
  flex: 0.5;
}

.metric-box--score {
  .metric-value {
    margin-top: 10px;
    font-size: 32px;
  }
}

.metric-box--suggested-level {
  background-color: #FFCB00;
}

.metric-box--suggested-level, .metric-box--selected-level {  
  .metric-label {
    color: #000;
    font-size: 12px;
    font-weight: 700;
  }
  .metric-value {
    margin-top: 10px;
    font-size: 32px;
  }
}

.metric-box--blue {
  background-color: #EEF7F9;
  .metric-label {
    color: #000;
  }
}

.metric-box--light {
  background-color: #f5f5f5;
}

.metric-label {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.metric-value {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
}

.metric-subvalue {
  font-size: 12px;
  font-weight: 400;
  color: #888;
}

.inactive-row td {
  color: #9e9e9e;
  background-color: #fafafa;
}

.subfactor-justification {
  display: flex;
  gap: 8px;
  margin: 6px 0;
  padding: 8px 12px;
  background: #fff;
  border-left: 3px solid #cfd8e3;
  border-radius: 4px;
}

.subfactor-justification-icon {
  margin-top: 2px;
}

/* AI rationale provenance (PD-1301) */
.subfactor-justification-atlas {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: #4f46e5;
}
.subfactor-justification-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a98a8;
  margin-bottom: 2px;
}

.subfactor-justification-text {
  white-space: pre-wrap;
  line-height: 1.5;
  color: #444;
}
</style>
